import axios from "axios"
import AuthToken from "@/auth/auth-token"

class Rules {
  constructor() {
    const base = axios.create({
      baseURL: process.env.VUE_APP_RULES_MANAGEMENT_API,
      withCredentials: false,
      headers: {
        "Content-Type": "application/json",
      }
    })
    this.rules = base

    const tprManagerBase = axios.create({
      baseURL: process.env.VUE_APP_BASE_API,
      withCredentials: false,
      headers: {
        "Content-Type": "application/json",
      }
    })
    this.tprManager = tprManagerBase
  }

  async getRules(isStoreLevel = false){ 
    await AuthToken.setAuth(this.rules)
    return this.rules({
      url: '/rules',
      method: 'GET',
      params: {
        is_store_level: isStoreLevel,
      }
    })
  }

  async getRulesets(party_id){ 
    await AuthToken.setAuth(this.rules)
    return this.rules({
      url: '/rulesets',
      method: 'GET',
      params: {
        party_id: party_id
      }
    })
  }

  async updateRulesets(ruleBody){
    await AuthToken.setAuth(this.rules)
    return this.rules({
      url: '/rulesets',
      method: 'PUT',
      data: ruleBody
    })
  }

  async createRulesets(ruleBody) {
    await AuthToken.setAuth(this.rules)
    return this.rules({
      url: '/rulesets',
      method: 'POST',
      data: ruleBody
    })
  }

  async deleteRulesets(ruleset_id) {
    await AuthToken.setAuth(this.rules)
    return this.rules({
      url: '/rulesets',
      method: 'DELETE',
      params: {
        ruleset_id: ruleset_id
      }
    })
  }

  async getPartyRelationships(partyID) {
    await AuthToken.setAuth(this.rules)
    return this.rules({
      url: '/relationship',
      method: 'GET',
      params: {
        party_id: partyID,
        tenant: 'alliance-retail-group'
      }
    })
  }

  async getDepartments() {
    await AuthToken.setAuth(this.rules)
    return this.rules({
      url: '/departments',
      method: 'GET',
    })
  }

  async getCategories() {
    await AuthToken.setAuth(this.rules)
    return this.rules({
      url: '/categories',
      method: 'GET',
    })
  }

  async saveExclusion(id, updated_by, excluded) {
    await AuthToken.setAuth(this.rules)
    return this.rules({
      url: '/exclusion',
      method: 'PUT',
      params: {
        tpr_id: id, 
        updated_by: updated_by,
        excluded: excluded
      }
    })
  }

  async saveBulkExclusions(items) {
    await AuthToken.setAuth(this.rules)
    return this.rules({
      url: '/bulk-exclusion',
      method: 'PUT',
      data: items
    })
  }

  async getStores( user_email, tenant ) {
    await AuthToken.setAuth(this.rules)
    return this.rules({
      url: '/stores-by-email',
      method: 'GET',
      params: {
        email: user_email,
        tenant: tenant
      }
    })
  }

  async getStoreBatches(
    storeId,
    startDt,
    endDt,
    limit = 100,
    offset = 0
  ){
    await AuthToken.setAuth(this.rules)
    return this.rules({
      url: '/store-batch',
      method: 'GET',
      params: {
        store_id: storeId,
        start_dt: startDt,
        end_dt: endDt,
        limit: limit,
        offset: offset
      }
    })
  }

  async getShuttleStores(
    batchId,
    storeId,
    tprDate
  ){
    await AuthToken.setAuth(this.rules)
    return this.rules({
      url: '/shuttle-stores',
      method: 'GET',
      params: {
        batch_id: batchId,
        store_id: storeId,
        tpr_dt: tprDate
      }
    })
  }

  async getShuttleItems(
    storeIds,
    tprDate,
    itemIds
  ){
    await AuthToken.setAuth(this.rules)
    return this.rules({
      url: '/available-shuttle-store-items',
      method: 'POST',
      data: {items: itemIds, store_ids: storeIds, tpr_dt: tprDate}
    })
  }

  async getChangedItems(storeId, batchId) {
    await AuthToken.setAuth(this.rules);
    return this.rules({
      url: '/changed-items', 
      method: 'GET',
      params: {
        store_id: storeId,
        batch_id: batchId, 
      },
    });
  }

  async publishTprReport(batch_id){
    await AuthToken.setAuth(this.rules)
    return this.rules({
      url: '/publish-tpr-report',
      method: 'POST',
      params: {
        batch_id: batch_id
      }
    })
  }

  async syncStoreBatch(batch_id, store_id){
    await AuthToken.setAuth(this.rules)
    return this.rules({
      url: '/sync-store-tpr-report',
      method: 'POST',
      params : {
        batch_id: batch_id,
        store_id: store_id
      }
    })
  }

  async getComponents(batchId, shipper_item_id, limit = 100, offset = 0) {
    await AuthToken.setAuth(this.rules)
    return this.rules({
      url: `/tpr_component/${batchId}`,
      method: 'GET',
      params: {
        shipper_item_id: shipper_item_id,
        limit,
        offset
      }
    })
  }

  async getUniqueValues(batch_id, column) {
    await AuthToken.setAuth(this.rules)
    return this.rules({
      url: `/unique_batch_values/${batch_id}`,
      method: 'POST',
      params: {
        column: column
      }
    })
  }

  // call for the store side srp and multiplier. Its different from the ad group level
  async storeSrpMultiplier(requestBody){
    await AuthToken.setAuth(this.rules)
    return this.rules({
      url: '/store-item-update',
      method: 'PUT',
      data: requestBody
    })
  }

  async getPromoPromotionPrice(batchId, itemId, promotionCategory) {
    await AuthToken.setAuth(this.rules)
    return this.rules({
      url: '/promo_promotion_price',
      method: 'GET',
      params: {
        batch_id: batchId,
        item_id: itemId,
        promotion_category: promotionCategory
      }
    })
  }

  async getEnforcedPromos(batchId, storeId, item_id) {
    await AuthToken.setAuth(this.rules)
    return this.rules({
      url: `/enforced_promos/${batchId}`,
      method: 'GET',
      params: {
        store_id: storeId,
        item_id: item_id
      }
    })
  }

  async exportStoreBatch(storeBatchId, batchType, tprDate, storeName, tprBatchId, storeId, headers = []) {
    headers.shift()
    headers = headers.filter(header => header !== 'early_buy_allowance' && header !== 'promo_info' && header !== 'comp_pricing_mode')
    const headersString = headers.join(',')

    await AuthToken.setAuth(this.rules)
    return this.rules({
      url: `/export/${tprBatchId}`,
      method: 'GET',
      params: {
        store_batch_id: storeBatchId,
        batch_type: batchType,
        tpr_date: tprDate,
        store_name: storeName,
        store_id: storeId,
        header_string: headersString
      }
    })
  }
}

export default new Rules()